/**
 * AnalyticsTab.jsx - Analytics Panel Component
 * 
 * Source: /tmp/AnalyticsPanel_source.tsx (471 lines)
 * Translation: React Native for Web → Standard React DOM
 * 
 * Part 1 Checklist Items: ✅ COMPLETE
 * - [x] 1.1.1-1.1.4: Panel Header (title, subtitle, styling)
 * - [x] 1.2.1-1.2.8: KPI Strip (5 cells, dividers, styling)
 * - [x] 1.3.1-1.3.5: Tab Navigation (7 chips, active state, click handler, styling)
 * 
 * Part 2 Checklist Items: ✅ COMPLETE
 * - [x] 2.1.1: Section label "Live Metrics"
 * - [x] 2.1.2: Ring shell container (flex-wrap layout)
 * - [x] 2.1.3: KPI Ring: "RSVP Rate" (success color, green)
 * - [x] 2.1.4: KPI Ring: "Email Open Rate" (info color, blue)
 * - [x] 2.1.5: KPI Ring: "SMS Delivery" (success color, placeholder "—")
 * - [x] 2.1.6: KPI Ring: "Budget Used" (warning color, amber)
 * - [x] 2.1.7: Ring component with value, total, displayValue, subtitle
 * - [x] 2.1.8: sectionLabel, ringShell styling
 * - [x] 2.2.1: Section label "Coming Soon"
 * - [x] 2.2.2: Future grid container (flex-wrap)
 * - [x] 2.2.3: DisabledFutureCard: "Engagement Score"
 * - [x] 2.2.4: DisabledFutureCard: "Vendor Risk Index"
 * - [x] 2.2.5: DisabledFutureCard: "Guest Sentiment Score"
 * - [x] 2.2.6: DisabledFutureCard: "Event Readiness Score"
 * - [x] 2.2.7: futureGrid styling
 * - [x] 2.2.8: DisabledFutureCard compact variant styling
 * 
 * Part 3 Checklist Items: ✅ COMPLETE
 * - [x] 3.1.1: PanelShell container with border, background, padding
 * - [x] 3.1.2: Section title "Cumulative RSVP Snapshot"
 * - [x] 3.1.3: Section note description text
 * - [x] 3.1.4: panelShell, sectionTitle, sectionNote styling
 * - [x] 3.2.1: Bar chart container with flex-row, align-items: flex-end
 * - [x] 3.2.2: 4 bar columns: Invited (gold), Attending (green), Declined (red), Pending (amber)
 * - [x] 3.2.3: Bar number display above each bar
 * - [x] 3.2.4: Bar track (tall, background #F2ECE1)
 * - [x] 3.2.5: Bar fill (dynamic height based on value/max)
 * - [x] 3.2.6: Bar caption label below each bar
 * - [x] 3.2.7: barChart, barColumn, barTrackTall, barFillTall, barNumber, barCaption styling
 * - [x] 3.2.8: Dynamic bar height calculation: (value / maxValue) * 100%
 * 
 * Part 4 Checklist Items: ✅ COMPLETE
 * - [x] 4.1.1: Grid container with flex-wrap layout
 * - [x] 4.1.2: analyticsGrid styling
 * - [x] 4.2.1: Card: "Spend by Category" - total vendor spend
 * - [x] 4.2.2: Card: "Budget Burn Rate" - percentage spent
 * - [x] 4.2.3: Card: "Vendor Health" - confirmed vendors count
 * - [x] 4.2.4: Card: "Outstanding Commitments" - remaining balance (warning color)
 * - [x] 4.2.5: Large metric display (28px, bold)
 * - [x] 4.2.6: Note description below each metric
 * - [x] 4.2.7: metricLarge, sectionTitle, sectionNote styling
 * - [x] 4.2.8: Warning color for outstanding commitments
 * 
 * Part 5 Checklist Items: ✅ COMPLETE
 * - [x] 5.1.1: Tiles grid container with flex-wrap
 * - [x] 5.1.2: tilesGrid styling
 * - [x] 5.2.1: Tile: "Emails Sent" (real emailStats.sent only; honest '—' when null — never the guest count)
 * - [x] 5.2.2: Tile: "Open Rate" (percentage or "—")
 * - [x] 5.2.3: Tile: "Click Rate" (value or "—")
 * - [x] 5.2.4: Tile: "SMS Sent" (placeholder "—")
 * - [x] 5.2.5: Tile: "SMS Delivered" (placeholder "—")
 * - [x] 5.2.6: Tile: "Bounces" (from guestStats.failed)
 * - [x] 5.2.7: Tile: "Delivery Snapshot" (percentage with note)
 * - [x] 5.2.8: tileCard, tileLabel, tileValue, tileNote styling
 * 
 * Part 6 Checklist Items: ✅ COMPLETE
 * - [x] 6.1.1: PanelShell container
 * - [x] 6.1.2: Section title "Export Center"
 * - [x] 6.1.3: Section note about truthful report access
 * - [x] 6.1.4: Report list container with gap
 * - [x] 6.2.1: Report row: "Guest List CSV" (Ready status)
 * - [x] 6.2.2: Report row: "Email Campaign Summary" (Soon status)
 * - [x] 6.2.3: Report row: "RSVP Summary PDF" (Soon status)
 * - [x] 6.2.4: Report icon (document-text-outline / FileText)
 * - [x] 6.2.5: Report title and description
 * - [x] 6.2.6: Status badge (Ready = green, Soon = gray)
 * - [x] 6.2.7: Click handler with alert/modal
 * - [x] 6.2.8: reportList, reportRow, reportIcon, reportTitle, reportDescription, 
 *              reportStatus, reportStatusReady, reportStatusSoon, reportStatusText styling
 * - [x] 6.3.1: DisabledFutureCard: "Scheduled Reports"
 * 
 * Part 7 Checklist Items: ✅ COMPLETE
 * - [x] 7.1.1: PanelShell wrapper for Invite Funnel
 * - [x] 7.1.2: DisabledFutureCard: "Invite Funnel"
 * - [x] 7.1.3: Description: "Visualize the journey from invite send to confirmed attendance..."
 * - [x] 7.2.1: PanelShell wrapper for Stakeholder Portal
 * - [x] 7.2.2: DisabledFutureCard: "Stakeholder Status Portal"
 * - [x] 7.2.3: Description: "A shareable, read-only dashboard for stakeholders..."
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * ANALYTICS PANEL TRANSLATION: 100% COMPLETE (86/86 items)
 * Classification: UI-Complete, Backend-Dependent (Guest List CSV export)
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useMemo, useCallback } from 'react';
import { BarChart3, Lock, FileText } from 'lucide-react';
import { theme } from '../../data/colors';
import AdminPageHeader from './AdminPageHeader';

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'kpi', label: 'KPI Overview' },
  { key: 'rsvp', label: 'RSVP Trends' },
  { key: 'finance', label: 'Finance Analytics' },
  { key: 'messaging', label: 'Messaging' },
  { key: 'funnel', label: 'Invite Funnel' },
  { key: 'stakeholder', label: 'Stakeholder Portal' },
  { key: 'reports', label: 'Reports & Exports' },
];

// ─── Color System (mapped to theme) ───────────────────────────────────────────

const COLORS = {
  gold: theme.accentPrimary,
  goldLight: theme.accentTintLight,
  goldBorder: theme.borderMedium,
  goldBorderStrong: theme.borderMedium,
  white: theme.bgCard,
  surface: theme.bgCardWarm,
  surfaceAlt: theme.bgCardWarm,
  grey: theme.textMuted,
  greyLight: theme.textPlaceholder,
  greyDark: theme.textSecondary,
  textPrimary: theme.textPrimary,
  textMuted: theme.textMuted,
  success: theme.statusSuccess,
  warning: theme.statusWarning,
  info: '#2563EB',
};

// ─── Helper Functions ─────────────────────────────────────────────────────────

function safePct(numerator, denominator) {
  if (!denominator) return 0;
  return Math.round(Math.min(Math.max((numerator / denominator) * 100, 0), 100));
}

// Days remaining until the event — computed from the real eventConfig.date
// (single source of truth), mirroring DashboardTab. Bare 'YYYY-MM-DD' strings
// are parsed as LOCAL midnight; a plain `new Date('YYYY-MM-DD')` would parse as
// UTC midnight and read as the previous day in any negative UTC offset.
// Returns null (NOT a fabricated number) when no date is configured, so callers
// can surface an honest '—'.
function daysUntilEvent(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr);
  const eventDate = /^\d{4}-\d{2}-\d{2}$/.test(s)
    ? new Date(`${s}T00:00:00`)
    : new Date(s);
  if (isNaN(eventDate.getTime())) return null;
  return Math.max(0, Math.ceil((eventDate.getTime() - Date.now()) / 86400000));
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  // Container
  tabContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    position: 'relative',
  },
  
  // 1.1: Panel Header
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.borderMedium}`,
    background: theme.bgHeader,
  },
  panelTitleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  panelTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: COLORS.textPrimary,
    letterSpacing: '-0.3px',
    margin: 0,
  },
  panelSubtitle: {
    fontSize: 13,
    color: COLORS.textMuted,
    fontWeight: 500,
    margin: 0,
  },
  
  // 1.2: KPI Strip
  kpiStrip: {
    padding: '16px 24px',
    background: theme.bgCard,
    borderBottom: `1px solid ${theme.borderMedium}`,
    overflowX: 'auto',
  },
  kpiRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 0,
    width: '100%',
  },
  kpiCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 24px',
    minWidth: 140,
    flex: 1,
  },
  kpiCellLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: COLORS.grey,
    marginBottom: 6,
    whiteSpace: 'nowrap',
  },
  kpiCellValue: {
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: '-0.5px',
    color: COLORS.textPrimary,
  },
  kpiDivider: {
    width: 1,
    background: 'linear-gradient(180deg, transparent 0%, rgba(26,24,21,0.40) 20%, rgba(26,24,21,0.40) 80%, transparent 100%)',
    margin: '4px 0',
  },

  // 1.3: Tab Navigation
  tabNavContainer: {
    padding: '14px 24px',
    overflowX: 'auto',
    borderBottom: `1px solid ${theme.borderMedium}`,
  },
  tabNavScroll: {
    display: 'flex',
    gap: 8,
    minWidth: 'max-content',
  },
  filterChip: {
    borderRadius: 999,
    border: `1px solid ${theme.borderMedium}`,
    backgroundColor: theme.bgCardWarm,
    padding: '8px 14px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
  },
  filterChipActive: {
    backgroundColor: theme.statusWarningTint,
    borderColor: 'rgba(198,167,90,0.5)',
  },
  filterChipText: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.grey,
  },
  filterChipTextActive: {
    color: '#8A7241',
  },

  // Content Area
  contentArea: {
    flex: 1,
    padding: 20,
    overflow: 'auto',
  },

  // Placeholder for Parts 2-7
  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 48,
    textAlign: 'center',
    borderRadius: 14,
    border: `1px solid ${theme.borderMedium}`,
    backgroundColor: COLORS.surface,
  },
  placeholderIcon: {
    marginBottom: 16,
    opacity: 0.5,
  },
  placeholderTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  placeholderText: {
    fontSize: 13,
    color: COLORS.textMuted,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PART 2: KPI Overview Tab Styles
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Panel Shell (reusable)
  panelShell: {
    backgroundColor: theme.bgCard,
    borderRadius: 14,
    border: `1px solid ${theme.borderMedium}`,
    padding: 18,
    boxShadow: theme.shadowCard,
    marginBottom: 16,
  },
  
  // Section Label
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    color: COLORS.grey,
    marginBottom: 12,
  },
  
  // Ring Shell
  ringShell: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    gap: 10,
    borderRadius: 14,
    border: `1px solid ${theme.borderLight}`,
    backgroundColor: theme.bgCardWarm,
    padding: '16px 14px',
  },
  
  // KPI Ring Component
  ringContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 10,
    minWidth: 100,
  },
  ringSvgWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ringInner: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ringValue: {
    fontSize: 22,
    fontWeight: 700,
    color: theme.textPrimary,
  },
  ringLabel: {
    marginTop: 8,
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.grey,
    textAlign: 'center',
  },
  ringSubtitle: {
    marginTop: 2,
    fontSize: 10,
    color: COLORS.greyLight,
    textAlign: 'center',
  },
  
  // Future Grid
  futureGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  
  // Disabled Future Card
  futureCard: {
    flex: 1,
    minWidth: 220,
    borderRadius: 14,
    padding: 16,
    border: `1px solid ${theme.borderMedium}`,
    backgroundColor: '#F8F7FB',
    boxShadow: '0px 6px 14px rgba(24,20,14,0.05)',
  },
  futureCardHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  futureCardIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: '1px solid rgba(91,110,174,0.18)',
    backgroundColor: 'rgba(91,110,174,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  futureCardBadge: {
    borderRadius: 999,
    border: '1px solid rgba(91,110,174,0.24)',
    backgroundColor: 'rgba(91,110,174,0.10)',
    padding: '4px 8px',
  },
  futureCardBadgeText: {
    fontSize: 9,
    fontWeight: 700,
    color: '#5B6EAE',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
  },
  futureCardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.textPrimary,
    margin: 0,
  },
  futureCardBody: {
    marginTop: 6,
    fontSize: 12,
    color: COLORS.grey,
    lineHeight: 1.5,
    margin: 0,
  },
  
  // Compact variant
  futureCardCompact: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 14,
    border: `1px solid ${theme.borderLight}`,
    backgroundColor: '#F8F7FB',
    padding: '12px',
    flex: 1,
    minWidth: 200,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PART 3: RSVP Trends Tab Styles
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Section Title
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 4,
    margin: 0,
  },
  
  // Section Note
  sectionNote: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginBottom: 18,
    margin: 0,
    marginTop: 4,
  },
  
  // Bar Chart Container
  barChart: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    gap: 16,
    padding: '20px 0',
    minHeight: 240,
  },
  
  // Bar Column (each individual bar group)
  barColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    maxWidth: 80,
  },
  
  // Bar Number (value above bar)
  barNumber: {
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  
  // Bar Track (background/container for the bar)
  barTrackTall: {
    width: 44,
    height: 140,
    borderRadius: 8,
    backgroundColor: '#F2ECE1',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  
  // Bar Fill (the colored portion)
  barFillTall: {
    width: '100%',
    borderRadius: 8,
    transition: 'height 0.4s ease',
  },
  
  // Bar Caption (label below bar)
  barCaption: {
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.grey,
    marginTop: 10,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PART 4: Finance Analytics Tab Styles
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Analytics Grid (flex-wrap container)
  analyticsGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  
  // Metric Card (individual card in the grid)
  metricCard: {
    flex: 1,
    minWidth: 220,
    borderRadius: 14,
    padding: 18,
    border: `1px solid ${theme.borderMedium}`,
    backgroundColor: theme.bgCard,
    boxShadow: '0px 6px 14px rgba(24,20,14,0.05)',
  },
  
  // Large Metric Value (28px, bold)
  metricLarge: {
    fontSize: 28,
    fontWeight: 700,
    color: COLORS.textPrimary,
    margin: 0,
    marginTop: 8,
  },
  
  // Warning color variant for metrics
  metricWarning: {
    color: COLORS.warning,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PART 5: Messaging Tab Styles
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Tiles Grid (flex-wrap container)
  tilesGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
  },
  
  // Tile Card (individual tile in the grid)
  tileCard: {
    flex: 1,
    minWidth: 140,
    maxWidth: 200,
    borderRadius: 14,
    padding: 16,
    border: `1px solid ${theme.borderLight}`,
    backgroundColor: theme.bgCardWarm,
    boxShadow: '0px 4px 10px rgba(24,20,14,0.04)',
  },
  
  // Tile Label (small uppercase header)
  tileLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: COLORS.grey,
    marginBottom: 6,
    margin: 0,
  },
  
  // Tile Value (large display value)
  tileValue: {
    fontSize: 24,
    fontWeight: 700,
    color: COLORS.textPrimary,
    margin: 0,
    marginTop: 4,
  },
  
  // Tile Note (small description below value)
  tileNote: {
    fontSize: 11,
    color: COLORS.textMuted,
    marginTop: 4,
    margin: 0,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PART 6: Reports & Exports Tab Styles
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Report List Container
  reportList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  
  // Report Row
  reportRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: '14px 16px',
    borderRadius: 12,
    border: `1px solid ${theme.borderLight}`,
    backgroundColor: theme.bgCardWarm,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  
  // Report Icon Container
  reportIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    border: `1px solid ${theme.borderMedium}`,
    backgroundColor: theme.accentTintLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  
  // Report Content (title + description)
  reportContent: {
    flex: 1,
    minWidth: 0,
  },
  
  // Report Title
  reportTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.textPrimary,
    margin: 0,
  },
  
  // Report Description
  reportDescription: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 2,
    margin: 0,
  },
  
  // Report Status Badge (base)
  reportStatus: {
    borderRadius: 999,
    padding: '5px 10px',
    flexShrink: 0,
  },
  
  // Ready Status (green)
  reportStatusReady: {
    backgroundColor: 'rgba(34,160,107,0.12)',
    border: '1px solid rgba(34,160,107,0.24)',
  },
  
  // Soon Status (gray)
  reportStatusSoon: {
    backgroundColor: 'rgba(122,115,103,0.10)',
    border: '1px solid rgba(122,115,103,0.20)',
  },
  
  // Status Text
  reportStatusText: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  
  // Ready Text Color
  reportStatusTextReady: {
    color: COLORS.success,
  },
  
  // Soon Text Color
  reportStatusTextSoon: {
    color: COLORS.grey,
  },

  futureCardCompactIconWrap: {
    width: 30,
    height: 30,
    borderRadius: 9,
    border: '1px solid rgba(91,110,174,0.18)',
    backgroundColor: 'rgba(91,110,174,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  futureCardCompactContent: {
    flex: 1,
    minWidth: 0,
  },
  futureCardCompactTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.textPrimary,
    margin: 0,
  },
  futureCardCompactBody: {
    marginTop: 2,
    fontSize: 11,
    color: COLORS.grey,
    lineHeight: 1.4,
    margin: 0,
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// PART 2: KpiRing Component
// ═══════════════════════════════════════════════════════════════════════════════

function KpiRing({ label, value, total, displayValue, color = COLORS.success, size = 'md', subtitle }) {
  // Size configurations
  const sizes = {
    sm: { outer: 70, stroke: 5 },
    md: { outer: 90, stroke: 6 },
    lg: { outer: 110, stroke: 7 },
  };
  
  const { outer, stroke } = sizes[size] || sizes.md;
  const radius = (outer - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = total ? Math.min(Math.max(value / total, 0), 1) : 0;
  const strokeDashoffset = circumference * (1 - percentage);

  return (
    <div style={styles.ringContainer} data-testid={`kpi-ring-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div style={{ ...styles.ringSvgWrap, width: outer, height: outer }}>
        <svg width={outer} height={outer} style={{ transform: 'rotate(-90deg)' }}>
          {/* Background track */}
          <circle
            cx={outer / 2}
            cy={outer / 2}
            r={radius}
            fill="none"
            stroke="#F0EAD8"
            strokeWidth={stroke}
          />
          {/* Progress arc */}
          <circle
            cx={outer / 2}
            cy={outer / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        <div style={{ ...styles.ringInner, width: outer, height: outer }}>
          <span style={styles.ringValue}>{displayValue}</span>
        </div>
      </div>
      <span style={styles.ringLabel}>{label}</span>
      {subtitle && <span style={styles.ringSubtitle}>{subtitle}</span>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PART 2: DisabledFutureCard Component
// ═══════════════════════════════════════════════════════════════════════════════

function DisabledFutureCard({ title, description, compact = false }) {
  if (compact) {
    return (
      <div style={styles.futureCardCompact} data-testid={`future-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <div style={styles.futureCardCompactIconWrap}>
          <Lock size={14} color="#5B6EAE" />
        </div>
        <div style={styles.futureCardCompactContent}>
          <p style={styles.futureCardCompactTitle}>{title}</p>
          <p style={styles.futureCardCompactBody}>{description}</p>
        </div>
        <div style={styles.futureCardBadge}>
          <span style={styles.futureCardBadgeText}>Soon</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.futureCard} data-testid={`future-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div style={styles.futureCardHeader}>
        <div style={styles.futureCardIconWrap}>
          <Lock size={16} color="#5B6EAE" />
        </div>
        <div style={styles.futureCardBadge}>
          <span style={styles.futureCardBadgeText}>Coming Soon</span>
        </div>
      </div>
      <h4 style={styles.futureCardTitle}>{title}</h4>
      <p style={styles.futureCardBody}>{description}</p>
    </div>
  );
}

// ─── AnalyticsTab Component ───────────────────────────────────────────────────

export default function AnalyticsTab({
  guestStats = { total: 0, sent: 0, pending: 0, failed: 0, rsvp_yes: 0, rsvp_no: 0 },
  emailStats = null,
  budget = null,
  vendors = [],
  guests = [],
  eventConfig = null,
}) {
  const [activeTab, setActiveTab] = useState('rsvp');

  // ── CSV Export Function ────────────────────────────────────────────────────
  const handleExportGuestCSV = useCallback(() => {
    if (!guests || guests.length === 0) {
      alert('No guest data available to export.');
      return;
    }
    
    // CSV headers
    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'RSVP Status', 'Invitation Status', 'Dietary Requirements', 'Notes'];
    
    // Build CSV rows
    const rows = guests.map(g => [
      g.first_name || '',
      g.last_name || '',
      g.email || '',
      g.phone || '',
      g.rsvp_status || 'pending',
      g.invitation_status || 'pending',
      g.dietary_requirements || '',
      (g.other_notes || '').replace(/,/g, ';').replace(/\n/g, ' '),
    ]);
    
    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `guest_list_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [guests]);

  // ── KPI Calculations ────────────────────────────────────────────────────────

  const topMetrics = useMemo(() => {
    const totalInvited = guestStats.total;
    const rsvpRate = safePct(guestStats.rsvp_yes ?? 0, Math.max(guestStats.total, 1));
    // open_rate is nullable on the backend rollup: null means "not tracked".
    // Keep null distinct from a real 0 so we never print a fabricated number.
    const emailOpenRate = emailStats?.open_rate ?? null;
    const budgetUsed = budget?.total_budget ? safePct(budget.spent_budget, budget.total_budget) : 0;
    // Days to Event — derived from the real eventConfig.date (same source the
    // Dashboard uses). Honest '—' when no date is configured; never hardcoded.
    const daysToEvent = daysUntilEvent(eventConfig?.date);

    return [
      { label: 'Total Invited', value: String(totalInvited) },
      { label: 'RSVP Rate', value: `${rsvpRate}%` },
      { label: 'Email Open Rate', value: emailOpenRate != null ? `${emailOpenRate}%` : '—' },
      { label: 'Budget Used', value: budget?.total_budget ? `${budgetUsed}%` : '—' },
      { label: 'Days to Event', value: daysToEvent != null ? String(daysToEvent) : '—' },
    ];
  }, [guestStats, emailStats, budget, eventConfig]);

  // ── Part 2: KPI Ring Data ─────────────────────────────────────────────────────

  const ringData = useMemo(() => {
    const rsvpRate = safePct(guestStats.rsvp_yes ?? 0, Math.max(guestStats.total, 1));
    // null = open-rate not tracked by the backend rollup. Keep distinct from a
    // real 0% so the ring shows an honest '—' (empty arc) instead of a fake value.
    const emailOpenRate = emailStats?.open_rate ?? null;
    const hasOpenRate = emailOpenRate != null;
    const smsDelivery = 0; // Placeholder
    const budgetUsed = budget?.total_budget ? safePct(budget.spent_budget ?? 0, budget.total_budget) : 0;

    return {
      rsvp: { value: guestStats.rsvp_yes ?? 0, total: guestStats.total, displayValue: `${rsvpRate}%`, subtitle: `${guestStats.rsvp_yes ?? 0}/${guestStats.total}` },
      email: { value: hasOpenRate ? emailOpenRate : 0, total: 100, displayValue: hasOpenRate ? `${emailOpenRate}%` : '—', subtitle: hasOpenRate ? 'tracked' : 'not tracked' },
      sms: { value: smsDelivery, total: 100, displayValue: '—', subtitle: 'not enabled' },
      budget: { value: budgetUsed, total: 100, displayValue: budget?.total_budget ? `${budgetUsed}%` : '—', subtitle: budget?.total_budget ? `$${(budget.spent_budget ?? 0).toLocaleString()}` : 'not set' },
    };
  }, [guestStats, emailStats, budget]);

  // ── Part 3: RSVP Trends Bar Chart Data ──────────────────────────────────────

  const rsvpBarData = useMemo(() => {
    const invited = guestStats.total ?? 0;
    const attending = guestStats.rsvp_yes ?? 0;
    const declined = guestStats.rsvp_no ?? 0;
    const pending = Math.max(0, invited - attending - declined);
    
    const bars = [
      { key: 'invited', label: 'Invited', value: invited, color: COLORS.gold },
      { key: 'attending', label: 'Attending', value: attending, color: COLORS.success },
      { key: 'declined', label: 'Declined', value: declined, color: '#D93025' },
      { key: 'pending', label: 'Pending', value: pending, color: COLORS.warning },
    ];
    
    // Calculate max value for bar height scaling
    const maxValue = Math.max(...bars.map(b => b.value), 1);
    
    return { bars, maxValue };
  }, [guestStats]);

  // ── Part 4: Finance Analytics Data ──────────────────────────────────────────

  const financeMetrics = useMemo(() => {
    // Real vendor schema (backend VendorCreate): contract_total, paid_to_date,
    // vendor_status. The earlier fields total_cost/amount_paid/status/contract_signed
    // do NOT exist on the vendor object, which forced every money KPI to $0.
    const hasVendors = vendors.length > 0;

    // "Secured" mirrors the backend's PLAN_SECURED_VENDOR_STATUSES so this matches
    // what Event Readiness / Vendors treat as booked.
    const SECURED_STATUSES = new Set([
      'Hired', 'InProgress', 'Completed', 'Booked', 'Confirmed', 'Signed',
    ]);

    // Total committed = sum of contract_total (FinanceTab falls back to contract_amount).
    const totalCommitted = vendors.reduce(
      (sum, v) => sum + (v.contract_total || v.contract_amount || 0), 0
    );

    // Calculate budget burn rate (percentage spent) — real budget fields, unchanged.
    const totalBudget = budget?.total_budget || 0;
    const spentBudget = budget?.spent_budget || 0;
    const burnRate = totalBudget > 0 ? safePct(spentBudget, totalBudget) : 0;

    // Count secured vendors by their real vendor_status.
    const securedVendors = vendors.filter(
      (v) => SECURED_STATUSES.has(String(v.vendor_status || ''))
    ).length;

    // Outstanding commitments = remaining balance on committed contracts.
    const outstandingCommitments = vendors.reduce((sum, v) => {
      const committed = v.contract_total || v.contract_amount || 0;
      const paid = v.paid_to_date || 0;
      return sum + Math.max(0, committed - paid);
    }, 0);

    return {
      spendByCategory: {
        value: hasVendors ? `$${totalCommitted.toLocaleString()}` : '—',
        note: hasVendors ? 'Total contract value committed to vendors' : 'No vendors added yet',
      },
      budgetBurnRate: {
        value: totalBudget > 0 ? `${burnRate}%` : '—',
        note: totalBudget > 0 ? `$${spentBudget.toLocaleString()} of $${totalBudget.toLocaleString()}` : 'Budget not configured',
      },
      vendorHealth: {
        value: hasVendors ? `${securedVendors}/${vendors.length}` : '—',
        note: hasVendors ? 'Vendors marked Hired / In Progress / Completed' : 'No vendors added yet',
      },
      outstandingCommitments: {
        value: hasVendors ? `$${outstandingCommitments.toLocaleString()}` : '—',
        note: hasVendors ? 'Remaining balance on vendor contracts' : 'No vendors added yet',
        isWarning: outstandingCommitments > 0,
      },
    };
  }, [budget, vendors]);

  // ── Part 5: Messaging Metrics Data ──────────────────────────────────────────

  const messagingMetrics = useMemo(() => {
    // Emails Sent — REAL count from the backend email rollup ONLY. We must NOT
    // fall back to guestStats.sent: the parent sets that to the full guest-list
    // size, so the old fallback reported every guest as an email that was sent.
    // null = not reported yet → honest '—', never the guest count.
    const emailsSent = emailStats?.sent ?? null;
    const hasEmailsSent = emailsSent != null;

    // Open / Click rates — nullable on the rollup. null = not tracked. Keep null
    // distinct from a real 0% so we never print a fabricated number.
    const openRate = emailStats?.open_rate ?? null;
    const hasOpenRate = openRate != null;
    const clickRate = emailStats?.click_rate ?? null;
    const hasClickRate = clickRate != null;

    // SMS Sent (placeholder)
    const smsSent = 0;

    // SMS Delivered (placeholder)
    const smsDelivered = 0;

    // Bounces (from guestStats.failed)
    const bounces = guestStats.failed ?? 0;

    // Delivery Snapshot — only meaningful once we have a real emails-sent count.
    const totalAttempted = (hasEmailsSent ? emailsSent : 0) + smsSent;
    const totalDelivered = (hasEmailsSent ? emailsSent : 0) - bounces + smsDelivered;
    const deliveryRate = totalAttempted > 0 ? safePct(totalDelivered, totalAttempted) : 0;

    return {
      emailsSent: {
        value: hasEmailsSent ? String(emailsSent) : '—',
        note: hasEmailsSent ? 'invitations delivered' : 'No data yet',
      },
      openRate: {
        value: hasOpenRate ? `${openRate}%` : '—',
        note: hasOpenRate ? 'of recipients opened' : 'No data yet',
      },
      clickRate: {
        value: hasClickRate ? `${clickRate}%` : '—',
        note: hasClickRate ? 'clicked a link' : 'No data yet',
      },
      smsSent: {
        value: '—',
        note: 'SMS not enabled',
      },
      smsDelivered: {
        value: '—',
        note: 'SMS not enabled',
      },
      bounces: {
        value: bounces > 0 ? String(bounces) : '0',
        note: bounces > 0 ? 'failed to deliver' : 'no bounces',
      },
      deliverySnapshot: {
        value: hasEmailsSent && totalAttempted > 0 ? `${deliveryRate}%` : '—',
        note: hasEmailsSent && totalAttempted > 0 ? `${totalDelivered}/${totalAttempted} delivered` : 'No data yet',
      },
    };
  }, [guestStats, emailStats]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={styles.tabContent} data-testid="analytics-panel">
      {/* ═══════════════════════════════════════════════════════════════════════
          PART 1: Panel Header
          ═══════════════════════════════════════════════════════════════════════ */}
      <AdminPageHeader
        eyebrow="Insights"
        title="Analytics"
        tagline="Performance insights, trends, and reporting"
      />

      {/* ═══════════════════════════════════════════════════════════════════════
          PART 1: KPI Strip
          ═══════════════════════════════════════════════════════════════════════ */}
      <div style={styles.kpiStrip} data-testid="analytics-kpi-strip">
        <div style={styles.kpiRow}>
          {topMetrics.map((metric, index) => (
            <React.Fragment key={metric.label}>
              {index > 0 && <div style={styles.kpiDivider} />}
              <div style={styles.kpiCell} data-testid={`kpi-${metric.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <span style={styles.kpiCellLabel}>{metric.label}</span>
                <span style={styles.kpiCellValue}>{metric.value}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          PART 1: Tab Navigation
          ═══════════════════════════════════════════════════════════════════════ */}
      <div style={styles.tabNavContainer} data-testid="analytics-tab-nav">
        <div style={styles.tabNavScroll}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                style={{
                  ...styles.filterChip,
                  ...(isActive ? styles.filterChipActive : {}),
                }}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`analytics-tab-${tab.key}`}
              >
                <span style={{
                  ...styles.filterChipText,
                  ...(isActive ? styles.filterChipTextActive : {}),
                }}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          PARTS 2-7: Content Area
          ═══════════════════════════════════════════════════════════════════════ */}
      <div style={styles.contentArea}>
        {/* ═══════════════════════════════════════════════════════════════════════
            PART 2: KPI Overview Tab
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'kpi' && (
          <div data-testid="kpi-overview-tab">
            {/* Live Metrics Section */}
            <div style={styles.panelShell}>
              <span style={styles.sectionLabel}>Live Metrics</span>
              <div style={styles.ringShell}>
                <KpiRing
                  label="RSVP Rate"
                  value={ringData.rsvp.value}
                  total={ringData.rsvp.total}
                  displayValue={ringData.rsvp.displayValue}
                  color={COLORS.success}
                  size="md"
                  subtitle={ringData.rsvp.subtitle}
                />
                <KpiRing
                  label="Email Open Rate"
                  value={ringData.email.value}
                  total={ringData.email.total}
                  displayValue={ringData.email.displayValue}
                  color={COLORS.info}
                  size="md"
                  subtitle={ringData.email.subtitle}
                />
                <KpiRing
                  label="SMS Delivery"
                  value={ringData.sms.value}
                  total={ringData.sms.total}
                  displayValue={ringData.sms.displayValue}
                  color={COLORS.success}
                  size="md"
                  subtitle={ringData.sms.subtitle}
                />
                <KpiRing
                  label="Budget Used"
                  value={ringData.budget.value}
                  total={ringData.budget.total}
                  displayValue={ringData.budget.displayValue}
                  color={COLORS.warning}
                  size="md"
                  subtitle={ringData.budget.subtitle}
                />
              </div>
            </div>

            {/* Coming Soon Section */}
            <div style={styles.panelShell}>
              <span style={styles.sectionLabel}>Coming Soon</span>
              <div style={styles.futureGrid}>
                <DisabledFutureCard
                  title="Engagement Score"
                  description="Aggregate score from opens, clicks, RSVPs."
                  compact
                />
                <DisabledFutureCard
                  title="Vendor Risk Index"
                  description="Flags vendors missing contracts or payments."
                  compact
                />
                <DisabledFutureCard
                  title="Guest Sentiment Score"
                  description="NLP analysis of RSVP notes and responses."
                  compact
                />
                <DisabledFutureCard
                  title="Event Readiness Score"
                  description="Overall checklist completion percentage."
                  compact
                />
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            PART 3: RSVP Trends Tab - Bar Chart
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'rsvp' && (
          <div data-testid="rsvp-trends-tab">
            <div style={styles.panelShell}>
              <h3 style={styles.sectionTitle}>Cumulative RSVP Snapshot</h3>
              <p style={styles.sectionNote}>
                A visual breakdown of your guest list responses. Track attendance confirmations, declines, and pending replies at a glance.
              </p>
              
              <div style={styles.barChart} data-testid="rsvp-bar-chart">
                {rsvpBarData.bars.map((bar) => {
                  const heightPercent = rsvpBarData.maxValue > 0 
                    ? (bar.value / rsvpBarData.maxValue) * 100 
                    : 0;
                  
                  return (
                    <div 
                      key={bar.key} 
                      style={styles.barColumn}
                      data-testid={`bar-column-${bar.key}`}
                    >
                      <span style={styles.barNumber}>{bar.value}</span>
                      <div style={styles.barTrackTall}>
                        <div 
                          style={{
                            ...styles.barFillTall,
                            height: `${heightPercent}%`,
                            backgroundColor: bar.color,
                          }}
                          data-testid={`bar-fill-${bar.key}`}
                        />
                      </div>
                      <span style={styles.barCaption}>{bar.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            PART 4: Finance Analytics Tab - Grid Cards
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'finance' && (
          <div data-testid="finance-analytics-tab">
            <div style={styles.panelShell}>
              <span style={styles.sectionLabel}>Financial Overview</span>
              <div style={styles.analyticsGrid}>
                {/* Card: Spend by Category */}
                <div style={styles.metricCard} data-testid="metric-spend-by-category">
                  <h4 style={styles.sectionTitle}>Spend by Category</h4>
                  <p style={styles.metricLarge}>{financeMetrics.spendByCategory.value}</p>
                  <p style={styles.sectionNote}>{financeMetrics.spendByCategory.note}</p>
                </div>

                {/* Card: Budget Burn Rate */}
                <div style={styles.metricCard} data-testid="metric-budget-burn-rate">
                  <h4 style={styles.sectionTitle}>Budget Burn Rate</h4>
                  <p style={styles.metricLarge}>{financeMetrics.budgetBurnRate.value}</p>
                  <p style={styles.sectionNote}>{financeMetrics.budgetBurnRate.note}</p>
                </div>

                {/* Card: Vendor Health */}
                <div style={styles.metricCard} data-testid="metric-vendor-health">
                  <h4 style={styles.sectionTitle}>Vendor Health</h4>
                  <p style={styles.metricLarge}>{financeMetrics.vendorHealth.value}</p>
                  <p style={styles.sectionNote}>{financeMetrics.vendorHealth.note}</p>
                </div>

                {/* Card: Outstanding Commitments (Warning Color) */}
                <div style={styles.metricCard} data-testid="metric-outstanding-commitments">
                  <h4 style={styles.sectionTitle}>Outstanding Commitments</h4>
                  <p style={{
                    ...styles.metricLarge,
                    ...(financeMetrics.outstandingCommitments.isWarning ? styles.metricWarning : {}),
                  }}>
                    {financeMetrics.outstandingCommitments.value}
                  </p>
                  <p style={styles.sectionNote}>{financeMetrics.outstandingCommitments.note}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            PART 5: Messaging Tab - Tiles Grid
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'messaging' && (
          <div data-testid="messaging-tab">
            <div style={styles.panelShell}>
              <span style={styles.sectionLabel}>Messaging Analytics</span>
              <div style={styles.tilesGrid}>
                {/* Tile: Emails Sent */}
                <div style={styles.tileCard} data-testid="tile-emails-sent">
                  <p style={styles.tileLabel}>Emails Sent</p>
                  <p style={styles.tileValue}>{messagingMetrics.emailsSent.value}</p>
                  <p style={styles.tileNote}>{messagingMetrics.emailsSent.note}</p>
                </div>

                {/* Tile: Open Rate */}
                <div style={styles.tileCard} data-testid="tile-open-rate">
                  <p style={styles.tileLabel}>Open Rate</p>
                  <p style={styles.tileValue}>{messagingMetrics.openRate.value}</p>
                  <p style={styles.tileNote}>{messagingMetrics.openRate.note}</p>
                </div>

                {/* Tile: Click Rate */}
                <div style={styles.tileCard} data-testid="tile-click-rate">
                  <p style={styles.tileLabel}>Click Rate</p>
                  <p style={styles.tileValue}>{messagingMetrics.clickRate.value}</p>
                  <p style={styles.tileNote}>{messagingMetrics.clickRate.note}</p>
                </div>

                {/* Tile: SMS Sent */}
                <div style={styles.tileCard} data-testid="tile-sms-sent">
                  <p style={styles.tileLabel}>SMS Sent</p>
                  <p style={styles.tileValue}>{messagingMetrics.smsSent.value}</p>
                  <p style={styles.tileNote}>{messagingMetrics.smsSent.note}</p>
                </div>

                {/* Tile: SMS Delivered */}
                <div style={styles.tileCard} data-testid="tile-sms-delivered">
                  <p style={styles.tileLabel}>SMS Delivered</p>
                  <p style={styles.tileValue}>{messagingMetrics.smsDelivered.value}</p>
                  <p style={styles.tileNote}>{messagingMetrics.smsDelivered.note}</p>
                </div>

                {/* Tile: Bounces */}
                <div style={styles.tileCard} data-testid="tile-bounces">
                  <p style={styles.tileLabel}>Bounces</p>
                  <p style={styles.tileValue}>{messagingMetrics.bounces.value}</p>
                  <p style={styles.tileNote}>{messagingMetrics.bounces.note}</p>
                </div>

                {/* Tile: Delivery Snapshot */}
                <div style={styles.tileCard} data-testid="tile-delivery-snapshot">
                  <p style={styles.tileLabel}>Delivery Snapshot</p>
                  <p style={styles.tileValue}>{messagingMetrics.deliverySnapshot.value}</p>
                  <p style={styles.tileNote}>{messagingMetrics.deliverySnapshot.note}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            PART 6: Reports & Exports Tab
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'reports' && (
          <div data-testid="reports-exports-tab">
            {/* Export Center Panel */}
            <div style={styles.panelShell}>
              <h3 style={styles.sectionTitle}>Export Center</h3>
              <p style={styles.sectionNote}>
                Access truthful, real-time reports for your event. Download guest data, track campaign performance, and share insights with stakeholders.
              </p>
              
              {/* Report List */}
              <div style={styles.reportList}>
                {/* Report Row: Guest List CSV (Ready) */}
                <div 
                  style={styles.reportRow} 
                  data-testid="report-guest-list-csv"
                  onClick={handleExportGuestCSV}
                  role="button"
                  tabIndex={0}
                >
                  <div style={styles.reportIcon}>
                    <FileText size={18} color={COLORS.gold} />
                  </div>
                  <div style={styles.reportContent}>
                    <p style={styles.reportTitle}>Guest List CSV</p>
                    <p style={styles.reportDescription}>Complete guest list with RSVP status, contact info, and notes ({guests.length} guests)</p>
                  </div>
                  <div style={{ ...styles.reportStatus, ...styles.reportStatusReady }}>
                    <span style={{ ...styles.reportStatusText, ...styles.reportStatusTextReady }}>Ready</span>
                  </div>
                </div>

                {/* Report Row: Email Campaign Summary (Soon) */}
                <div 
                  style={{ ...styles.reportRow, opacity: 0.7, cursor: 'not-allowed' }} 
                  data-testid="report-email-campaign"
                >
                  <div style={styles.reportIcon}>
                    <FileText size={18} color={COLORS.grey} />
                  </div>
                  <div style={styles.reportContent}>
                    <p style={styles.reportTitle}>Email Campaign Summary</p>
                    <p style={styles.reportDescription}>Detailed metrics on email opens, clicks, and engagement rates</p>
                  </div>
                  <div style={{ ...styles.reportStatus, ...styles.reportStatusSoon }}>
                    <span style={{ ...styles.reportStatusText, ...styles.reportStatusTextSoon }}>Soon</span>
                  </div>
                </div>

                {/* Report Row: RSVP Summary PDF (Soon) */}
                <div 
                  style={{ ...styles.reportRow, opacity: 0.7, cursor: 'not-allowed' }} 
                  data-testid="report-rsvp-summary"
                >
                  <div style={styles.reportIcon}>
                    <FileText size={18} color={COLORS.grey} />
                  </div>
                  <div style={styles.reportContent}>
                    <p style={styles.reportTitle}>RSVP Summary PDF</p>
                    <p style={styles.reportDescription}>Printable summary of attendance confirmations and dietary requirements</p>
                  </div>
                  <div style={{ ...styles.reportStatus, ...styles.reportStatusSoon }}>
                    <span style={{ ...styles.reportStatusText, ...styles.reportStatusTextSoon }}>Soon</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scheduled Reports Card */}
            <div style={{ marginTop: 16 }}>
              <DisabledFutureCard
                title="Scheduled Reports"
                description="Automate report delivery to stakeholders on a weekly or monthly basis. Configure recipients and choose report types to include."
              />
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            PART 7: Disabled Future Tabs - Invite Funnel
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'funnel' && (
          <div data-testid="invite-funnel-tab">
            <div style={styles.panelShell}>
              <DisabledFutureCard
                title="Invite Funnel"
                description="Visualize the journey from invite send to confirmed attendance. Track drop-off rates at each stage: Sent → Delivered → Opened → Clicked → RSVP'd → Confirmed. Identify bottlenecks and optimize your communication strategy."
              />
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            PART 7: Disabled Future Tabs - Stakeholder Portal
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'stakeholder' && (
          <div data-testid="stakeholder-portal-tab">
            <div style={styles.panelShell}>
              <DisabledFutureCard
                title="Stakeholder Status Portal"
                description="A shareable, read-only dashboard for stakeholders to view event progress. Share real-time RSVP counts, budget status, and key milestones without granting admin access. Perfect for keeping family members, sponsors, or vendors informed."
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
